import { useMemo, useState } from "react";
import { Figure, KpiStrip, Tile } from "../components/tile.js";
import { RulePanel, type RuleHistory } from "../components/rule-panel.js";

/** The rule's name, with its code underneath when the two differ. */
function RuleName({ row }: { readonly row: AnalyticsRow }) {
  const name = row.rule_name ?? row.rule_id;
  return (
    <>
      <strong>{name}</strong>
      {name === row.rule_id ? null : (
        <small className="muted"> {row.rule_id}</small>
      )}
    </>
  );
}

export interface AnalyticsRow {
  readonly sport: string;
  readonly rule_id: string;
  /** What the rule bets, from the API's name map; the code when unnamed. */
  readonly rule_name?: string;
  readonly bookmaker: string;
  readonly sport_scoped: boolean;
  readonly rule_scoped: boolean;
  readonly bookmaker_scoped: boolean;
  readonly bets: number;
  readonly settled: number;
  readonly wins: number;
  readonly losses: number;
  readonly turnover_cents: number | null;
  readonly profit_cents: number | null;
  readonly roi: number | null;
  readonly strike_rate: number | null;
  readonly avg_placed_price: number | null;
  readonly avg_price_slippage: number | null;
  readonly worse_than_quoted: number;
  readonly avg_price_clv: number | null;
  readonly price_clv_n: number;
  readonly beat_close: number;
  /** Bets whose price moved against us by the close. */
  readonly lost_close?: number;
  /** Bets where the whole market's no-vig probability for our side rose (or
   * fell) between the signal and the close: agreement free of line shopping. */
  readonly market_toward?: number;
  readonly market_against?: number;
  readonly expected_profit_cents: number | null;
  readonly expected_n: number;
  /** A tracked rule: graded and closed, never staked or in the totals. */
  readonly tracked?: boolean;
  /** Entry over close minus one, same-line closes only. */
  readonly clv_pct?: number | null;
  readonly clv_pct_n?: number;
  /** Entry price x no-vig closing probability - 1, at the bet's own line:
   * the bet's expected return at the market's final fair price. */
  /** The rule's own research record, from its release registry. */
  readonly bt_strike_rate?: number | null;
  readonly bt_roi?: number | null;
  readonly bt_bets?: number | null;
  readonly bt_basis?: string | null;
  readonly fair_clv_pct?: number | null;
  readonly fair_clv_n?: number;
  readonly fair_beat_close?: number;
  /** Each bet's return per unit staked: mean, spread and count. */
  readonly return_avg?: number | null;
  readonly return_sd?: number | null;
  readonly return_n?: number;
  /** The market the rule bets, by name ("Made Threes"). Rule rows only. */
  readonly market?: string | null;
}

/** Standard normal CDF (Abramowitz-Stegun 7.1.26, error < 1.5e-7). */
function normalCdf(z: number): number {
  const t = 1 / (1 + (0.3275911 * Math.abs(z)) / Math.SQRT2);
  const erf =
    1 -
    t *
      (0.254829592 +
        t *
          (-0.284496736 +
            t * (1.421413741 + t * (-1.453152027 + t * 1.061405429)))) *
      Math.exp(-(z * z) / 2);
  return z >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
}

/**
 * Chance a rule is genuinely profitable: P(true return per unit > 0) from
 * each bet's own return, so a mix of $1.50 and $4.00 bets is tested as it is
 * rather than as an average. 50% means the record says nothing either way.
 * Needs five graded bets; below that there is no spread to measure.
 */
export function chanceProfitable(
  returnAvg: number | null | undefined,
  returnSd: number | null | undefined,
  n: number | undefined,
): number | null {
  if (
    returnAvg == null ||
    returnSd == null ||
    n === undefined ||
    n < 5 ||
    Number(returnSd) <= 0
  )
    return null;
  return normalCdf(
    Number(returnAvg) / (Number(returnSd) / Math.sqrt(Number(n))),
  );
}

/**
 * Below this many settled bets a return is noise, so the figure is shown but
 * never presented as a result. Thirteen bets at 47% ROI is the exact trap this
 * exists to avoid.
 */
const READABLE_ROI_BETS = 200;
/** Closing-line value stabilises far sooner than realised return. */
const READABLE_CLV_BETS = 50;

const money = (cents: number | null): string =>
  cents === null
    ? "—"
    : `${cents < 0 ? "-" : ""}A$${Math.abs(cents / 100).toFixed(2)}`;

const percent = (value: number | null): string =>
  value === null ? "—" : `${(value * 100).toFixed(1)}%`;

/** Signed percentage from a fraction: 0.029 -> "+2.9%". */
const pct = (value: number | null | undefined): string =>
  value == null
    ? "—"
    : `${Number(value) >= 0 ? "+" : ""}${(Number(value) * 100).toFixed(1)}%`;

const signed = (value: number | null, digits = 3): string =>
  value === null ? "—" : `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;

type Grouping = "rule" | "bookmaker";

/** Strike rate minus the win rate the average odds need. */
const edgeOf = (row: AnalyticsRow): number | null =>
  row.avg_placed_price === null || row.strike_rate === null
    ? null
    : Number(row.strike_rate) - 1 / Number(row.avg_placed_price);

/** Of the bets where the whole market moved, the share that moved our way. */
const marketShare = (row: AnalyticsRow): number | null => {
  const moved = (row.market_toward ?? 0) + (row.market_against ?? 0);
  return moved === 0 ? null : (row.market_toward ?? 0) / moved;
};

type Column =
  | "name"
  | "sport"
  | "market"
  | "bets"
  | "turnover"
  | "pl"
  | "return"
  | "strike"
  | "odds"
  | "breakeven"
  | "edge"
  | "chance"
  | "slippage"
  | "clv"
  | "backtest"
  | "expected";

const TEXT_COLUMNS: readonly Column[] = ["name", "sport", "market"];

/** What each column sorts by. */
const SORT_VALUE: Record<
  Column,
  (row: AnalyticsRow, grouping: Grouping) => number | string | null
> = {
  name: (row, grouping) => (grouping === "rule" ? row.rule_id : row.bookmaker),
  sport: (row) => row.sport,
  market: (row) => row.market ?? null,
  bets: (row) => row.bets,
  turnover: (row) => row.turnover_cents,
  pl: (row) => row.profit_cents,
  return: (row) => row.roi,
  strike: (row) => row.strike_rate,
  odds: (row) => row.avg_placed_price,
  breakeven: (row) =>
    row.avg_placed_price === null ? null : 1 / Number(row.avg_placed_price),
  edge: edgeOf,
  chance: (row) =>
    chanceProfitable(row.return_avg, row.return_sd, row.return_n),
  slippage: (row) => row.avg_price_slippage,
  clv: marketShare,
  backtest: (row) => row.bt_roi ?? null,
  expected: (row) => (row.expected_n === 0 ? null : row.expected_profit_cents),
};

/**
 * Rows ordered by one column. Blanks always sink, whichever way the column
 * runs: a rule with no closing data is neither the best nor the worst on CLV.
 */
export function sortRows(
  rows: readonly AnalyticsRow[],
  column: Column,
  descending: boolean,
  grouping: Grouping = "rule",
): readonly AnalyticsRow[] {
  const value = (row: AnalyticsRow): number | string | null => {
    const raw = SORT_VALUE[column](row, grouping);
    if (raw === null || raw === undefined || raw === "") return null;
    return typeof raw === "string" ? raw : Number(raw);
  };
  return rows.toSorted((left, right) => {
    const a = value(left);
    const b = value(right);
    if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
    const order =
      typeof a === "string" || typeof b === "string"
        ? String(a).localeCompare(String(b))
        : a - b;
    return descending ? -order : order;
  });
}

export function AnalyticsPage({
  rows = [],
  sport = "ALL",
  loadRule,
}: {
  rows?: readonly AnalyticsRow[];
  sport?: string;
  /** Fetches one rule's history for its page. Absent, rules are not clickable. */
  loadRule?:
    ((sport: string, rule: string) => Promise<RuleHistory>) | undefined;
}) {
  const [grouping, setGrouping] = useState<Grouping>("rule");
  const [market, setMarket] = useState("ALL");
  const [sort, setSort] = useState<{
    column: Column;
    descending: boolean;
  } | null>(null);
  const [openRule, setOpenRule] = useState<{
    sport: string;
    rule: string;
    tracked: boolean;
  } | null>(null);

  const overall = useMemo(
    () =>
      rows.find(
        (row) =>
          !row.rule_scoped &&
          !row.bookmaker_scoped &&
          (sport === "ALL" ? !row.sport_scoped : row.sport_scoped),
      ),
    [rows, sport],
  );

  const inGrouping = useMemo(
    () =>
      rows.filter((row) =>
        grouping === "rule"
          ? row.rule_scoped && !row.bookmaker_scoped
          : row.bookmaker_scoped && !row.rule_scoped && !row.tracked,
      ),
    [rows, grouping],
  );
  // The market filter offers only what the table holds, so no choice empties
  // it. The sport is chosen by the page's own sport buttons.
  const markets = [
    ...new Set(inGrouping.flatMap((row) => (row.market ? [row.market] : []))),
  ].toSorted();
  const showMarkets = grouping === "rule" && markets.length > 0;

  const shown = inGrouping.filter(
    (row) => !showMarkets || market === "ALL" || row.market === market,
  );
  const sorted =
    sort === null
      ? shown.toSorted(
          (left, right) =>
            Number(left.tracked ?? false) - Number(right.tracked ?? false) ||
            (right.profit_cents ?? 0) - (left.profit_cents ?? 0),
        )
      : sortRows(shown, sort.column, sort.descending, grouping);
  // All sports at once reads as one block per sport, each sorted on its own,
  // never NBA and NFL rules interleaved (Moss, 31 Oct 2026).
  const sections = [...new Set(sorted.map((row) => row.sport))]
    .toSorted()
    .map((name) => ({
      sport: name,
      rows: sorted.filter((row) => row.sport === name),
    }));
  const sectioned = sport === "ALL" && sections.length > 1;

  const columns: readonly {
    readonly id: Column;
    readonly label: string;
    readonly title?: string;
  }[] = [
    { id: "name", label: grouping === "rule" ? "Rule" : "Bookmaker" },
    ...(sport === "ALL" ? [{ id: "sport" as const, label: "Sport" }] : []),
    ...(grouping === "rule"
      ? [{ id: "market" as const, label: "Market" }]
      : []),
    { id: "bets", label: "Bets" },
    { id: "turnover", label: "Turnover" },
    { id: "pl", label: "P/L" },
    { id: "return", label: "Return" },
    { id: "strike", label: "Strike" },
    { id: "odds", label: "Avg odds" },
    {
      id: "breakeven",
      label: "Break-even",
      title: "Win rate needed to break even at the average odds: 1 / odds",
    },
    { id: "edge", label: "Edge", title: "Strike rate minus break-even" },
    {
      id: "chance",
      label: "Chance profitable",
      title:
        "Chance the true win rate beats break-even, from wins and losses so far",
    },
    { id: "slippage", label: "Slippage" },
    {
      id: "clv",
      label: "CLV",
      title:
        "Whether the market agreed: of the bets where the whole market moved between the signal and the close, the share that moved toward our side (no-vig, averaged across books)",
    },
    {
      id: "backtest",
      label: "Backtest",
      title:
        "The rule's research record (walk-forward where available): strike rate and return",
    },
    { id: "expected", label: "Expected" },
  ];
  // First click: names A-Z, figures highest first. Second click flips.
  const sortBy = (column: Column) =>
    setSort((current) =>
      current?.column === column
        ? { column, descending: !current.descending }
        : { column, descending: !TEXT_COLUMNS.includes(column) },
    );

  const settled = overall?.settled ?? 0;

  return (
    <div className="board-grid">
      <KpiStrip columns={5}>
        <Figure
          label="Turnover"
          value={money(overall?.turnover_cents ?? null)}
          sub={`${overall?.bets ?? 0} bets`}
        />
        <Figure
          label="Profit / loss"
          value={money(overall?.profit_cents ?? null)}
          tone={(overall?.profit_cents ?? 0) < 0 ? "negative" : "positive"}
          sub={`${settled} settled`}
        />
        <Figure
          label="Return"
          value={percent(overall?.roi ?? null)}
          sub={settled < READABLE_ROI_BETS ? "not yet readable" : "on turnover"}
        />
        <Figure
          label="Expected"
          value={money(overall?.expected_profit_cents ?? null)}
          sub={
            (overall?.expected_n ?? 0) === 0
              ? "no EV recorded"
              : `from ${overall?.expected_n ?? 0} with an estimate`
          }
        />
        <Figure
          label="Market moved our way"
          value={
            (overall?.market_toward ?? 0) + (overall?.market_against ?? 0) === 0
              ? "—"
              : percent(
                  (overall?.market_toward ?? 0) /
                    ((overall?.market_toward ?? 0) +
                      (overall?.market_against ?? 0)),
                )
          }
          sub={
            (overall?.price_clv_n ?? 0) === 0 ||
            (overall?.market_toward ?? 0) + (overall?.market_against ?? 0) === 0
              ? "none captured"
              : `${overall?.market_toward ?? 0} toward · ${overall?.market_against ?? 0} against, whole market`
          }
        />
      </KpiStrip>

      {settled < READABLE_ROI_BETS ? (
        <div className="board-wide">
          <div className="value-check fail" role="status">
            <strong>
              {settled} settled bet{settled === 1 ? "" : "s"} — too few to read
              a return from
            </strong>
            <span>
              Returns need roughly {READABLE_ROI_BETS} settled bets before they
              mean anything, and closing value about {READABLE_CLV_BETS}. The
              figures above are shown so they can be watched, not concluded
              from.
            </span>
          </div>
        </div>
      ) : null}

      <Tile
        title="Breakdown"
        span={12}
        actions={
          <div className="sport-selector" role="group" aria-label="Group by">
            <button
              type="button"
              aria-pressed={grouping === "rule"}
              onClick={() => setGrouping("rule")}
            >
              By rule
            </button>
            <button
              type="button"
              aria-pressed={grouping === "bookmaker"}
              onClick={() => setGrouping("bookmaker")}
            >
              By bookmaker
            </button>
          </div>
        }
      >
        <div className="table-toolbar">
          {showMarkets ? (
            <label>
              <span>Market</span>
              <select
                value={market}
                onChange={(event) => setMarket(event.target.value)}
              >
                <option value="ALL">All markets</option>
                {markets.map((option) => (
                  <option key={option} value={option}>
                    {option} (
                    {inGrouping.filter((row) => row.market === option).length})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {/* Phones hide the column headers, so the sort lives here too. */}
          <label>
            <span>Sort</span>
            <select
              value={sort?.column ?? "default"}
              onChange={(event) =>
                event.target.value === "default"
                  ? setSort(null)
                  : sortBy(event.target.value as Column)
              }
            >
              <option value="default">Best P/L, tracked last</option>
              {columns.map((column) => (
                <option key={column.id} value={column.id}>
                  {column.label}
                </option>
              ))}
            </select>
          </label>
          {sort === null ? null : (
            <button
              type="button"
              className="sort-direction"
              aria-label="Reverse the sort"
              onClick={() =>
                setSort({ column: sort.column, descending: !sort.descending })
              }
            >
              {TEXT_COLUMNS.includes(sort.column)
                ? sort.descending
                  ? "▼ Z–A"
                  : "▲ A–Z"
                : sort.descending
                  ? "▼ High first"
                  : "▲ Low first"}
            </button>
          )}
        </div>
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  title={column.title}
                  aria-sort={
                    sort?.column !== column.id
                      ? undefined
                      : sort.descending
                        ? "descending"
                        : "ascending"
                  }
                >
                  <button
                    type="button"
                    className="th-sort"
                    onClick={() => sortBy(column.id)}
                  >
                    {column.label}
                    {sort?.column === column.id ? (
                      <span aria-hidden="true">
                        {sort.descending ? " ▼" : " ▲"}
                      </span>
                    ) : null}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  {inGrouping.length === 0
                    ? "No settled bets yet."
                    : "No rows match these filters."}
                </td>
              </tr>
            ) : (
              (sectioned ? sections : [{ sport: "", rows: sorted }]).flatMap(
                (section) => [
                  ...(sectioned
                    ? [
                        <tr
                          className="sport-group"
                          key={`group-${section.sport}`}
                        >
                          <th colSpan={columns.length}>
                            {section.sport}
                            <small>
                              {section.rows.length}{" "}
                              {grouping === "rule" ? "rules" : "books"}
                            </small>
                          </th>
                        </tr>,
                      ]
                    : []),
                  ...section.rows.map((row) => (
                    <tr
                      key={`${row.sport}-${row.rule_id}-${row.bookmaker}-${row.tracked === true ? "tracked" : "live"}`}
                    >
                      <td
                        data-label={grouping === "rule" ? "Rule" : "Bookmaker"}
                      >
                        {grouping === "rule" && loadRule !== undefined ? (
                          <button
                            type="button"
                            className="link-button"
                            onClick={() =>
                              setOpenRule({
                                sport: row.sport,
                                rule: row.rule_id,
                                tracked: row.tracked ?? false,
                              })
                            }
                          >
                            <RuleName row={row} />
                          </button>
                        ) : grouping === "rule" ? (
                          <RuleName row={row} />
                        ) : (
                          <strong>{row.bookmaker}</strong>
                        )}
                        {row.tracked ? (
                          <span className="badge">Tracked</span>
                        ) : null}
                      </td>
                      {sport === "ALL" ? (
                        <td data-label="Sport">{row.sport}</td>
                      ) : null}
                      {grouping === "rule" ? (
                        <td data-label="Market">{row.market ?? "—"}</td>
                      ) : null}
                      <td className="tabular" data-label="Bets">
                        {row.bets}
                      </td>
                      <td className="tabular" data-label="Turnover">
                        {money(row.turnover_cents)}
                      </td>
                      <td className="tabular" data-label="P/L">
                        <strong
                          className={
                            (row.profit_cents ?? 0) >= 0
                              ? "positive"
                              : "negative"
                          }
                        >
                          {money(row.profit_cents)}
                        </strong>
                      </td>
                      <td className="tabular" data-label="Return">
                        {percent(row.roi)}
                      </td>
                      <td className="tabular" data-label="Strike">
                        {percent(row.strike_rate)}
                        <small>
                          {row.wins}-{row.losses}
                        </small>
                      </td>
                      <td className="tabular" data-label="Avg odds">
                        {row.avg_placed_price === null
                          ? "—"
                          : `$${Number(row.avg_placed_price).toFixed(2)}`}
                      </td>
                      <td className="tabular" data-label="Break-even">
                        {row.avg_placed_price === null
                          ? "—"
                          : percent(1 / Number(row.avg_placed_price))}
                      </td>
                      <td className="tabular" data-label="Edge">
                        {row.avg_placed_price === null ||
                        row.strike_rate === null
                          ? "—"
                          : `${Number(row.strike_rate) - 1 / Number(row.avg_placed_price) >= 0 ? "+" : ""}${((Number(row.strike_rate) - 1 / Number(row.avg_placed_price)) * 100).toFixed(1)} pts`}
                      </td>
                      <td className="tabular" data-label="Chance profitable">
                        {percent(
                          chanceProfitable(
                            row.return_avg,
                            row.return_sd,
                            row.return_n,
                          ),
                        )}
                      </td>
                      <td className="tabular" data-label="Slippage">
                        {signed(row.avg_price_slippage)}
                        {row.worse_than_quoted > 0 ? (
                          <small>{row.worse_than_quoted} under quote</small>
                        ) : null}
                      </td>
                      <td className="tabular" data-label="CLV">
                        {(row.market_toward ?? 0) +
                          (row.market_against ?? 0) ===
                        0 ? (
                          "—"
                        ) : (
                          <>
                            {`${percent((row.market_toward ?? 0) / ((row.market_toward ?? 0) + (row.market_against ?? 0)))} our way`}
                            <small>
                              market {row.market_toward ?? 0}–
                              {row.market_against ?? 0} · own book{" "}
                              {row.beat_close}–{row.lost_close ?? 0}
                            </small>
                          </>
                        )}
                      </td>
                      <td className="tabular" data-label="Backtest">
                        {row.bt_strike_rate == null && row.bt_roi == null ? (
                          "—"
                        ) : (
                          <>
                            {row.bt_strike_rate == null
                              ? ""
                              : `${percent(Number(row.bt_strike_rate))} strike`}
                            {row.bt_roi == null
                              ? ""
                              : `${row.bt_strike_rate == null ? "" : " · "}${pct(Number(row.bt_roi))} return`}
                            <small>
                              {row.bt_bets == null
                                ? ""
                                : `${row.bt_bets} bets · `}
                              {row.bt_basis ?? ""}
                            </small>
                          </>
                        )}
                      </td>
                      <td className="tabular" data-label="Expected">
                        {row.expected_n === 0 ? (
                          <small>not recorded</small>
                        ) : (
                          money(row.expected_profit_cents)
                        )}
                      </td>
                    </tr>
                  )),
                ],
              )
            )}
          </tbody>
        </table>
      </Tile>
      {openRule === null || loadRule === undefined ? null : (
        <RulePanel
          key={`${openRule.sport}-${openRule.rule}`}
          sport={openRule.sport}
          rule={openRule.rule}
          tracked={openRule.tracked}
          load={loadRule}
          onClose={() => setOpenRule(null)}
        />
      )}
    </div>
  );
}
